import React, { useEffect, useState } from "react";
import {
  Row,
  Form,
  FormGroup,
  Button,
  Alert,
  Col,
  Container,
  Spinner,
  Modal,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import ReactGA from "react-ga4";
import { Field, Formik } from "formik";
import { groupBy, uniq } from "lodash";
import {
  updateJobTransaction,
  updateJobTransactionTagLevel,
} from "../../../docuclipper/api";
import { FormDoc } from "../../../docuclipper/DocuclipperTypes";
import { ReduxState } from "../../../redux";
import {
  fetchTls,
  setSelectedDocumentId,
  setSelectedRowNumber,
} from "../../../redux/reducers/TransactionManager";
import { createAlert } from "../../../redux/reducers/Alerts";
import { fetchIntegrations } from "../../../redux/reducers/Integration";
import {
  formikInput,
  isNullOrUndefined,
  sendGAEvent,
  updateObjKeyValue,
} from "../../../utils/utils";

import { ExportTypeDropdown } from "../DataArchive/TransactionManager/DownloadOptions/ExportTypeDropdown";
import { downloadSpreadsheetHelper } from "../DataArchive/TransactionManager/DownloadOptions/utils";

import PDFViewer from "../PdfViewer";
import { getJwt } from "../../../auth";
import DocumentSelector from "../DataArchive/TransactionManager/DocumentSelector";
import Select from "react-select";
import { getDocumentName } from "src/views/Pages/Analyze/AnalyzeTransactionsTable/utils";
import DocumentsDropdown from "../DataArchive/TransactionManager/DownloadOptions/DocumentsDropdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useHistory, useLocation } from "react-router-dom";
import { setHelpDrawerOpen } from "src/redux/reducers/HelpDrawer";
import HelpDrawer from "src/views/Docuclipper/Ratings/HelpDrawer";

export const ExportForm: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { job } = useSelector((state: ReduxState) => state.JobData);
  const { tls, selectedDocumentId, selectedRowNumber } = useSelector(
    (state: ReduxState) => state.TransactionManager,
  );
  const exportTo = useSelector(
    (state: ReduxState) => state.DownloadOptions.selectedExportType,
  );

  const { selectedL1, selectedL2 } = useSelector(
    (state: ReduxState) => state.TagLevel,
  );

  // Get l1 and l2 from URL query params
  const searchParams = new URLSearchParams(location.search);
  const urlL1 = searchParams.get("l1");
  const urlL2 = searchParams.get("l2");

  const handleAddForms = () => {
    if (urlL1 && urlL2) {
      history.push(
        `/extract/form?l1=${encodeURIComponent(urlL1)}&l2=${encodeURIComponent(urlL2)}`,
      );
    }
  };

  const [pdfURL, setPdfURL] = useState("");
  const [selectedPageNumber, setSelectedPageNumber] = useState(1);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);

  useEffect(() => {
    ReactGA.event("export_form_opened");
  }, []);

  const { data: jobDocs, loading } = useSelector(
    (state: ReduxState) => state.JobDocument,
  );

  const uniqueDocumentIds = Array.from(
    new Set((jobDocs || []).map((item) => item.documentId)),
  );
  const options = uniqueDocumentIds.map((docId) => ({
    value: docId,
    label: getDocumentName(jobDocs || [], docId),
  }));

  // Parse query parameters for new documents
  const queryParams = new URLSearchParams(window.location.search);
  const newDocumentsParam = queryParams.get("newDocuments");

  // Set default selection based on URL parameters
  useEffect(() => {
    if (selectedDocumentIds.length === 0 && (jobDocs || []).length > 0) {
      if (newDocumentsParam) {
        // If newDocuments param exists, select only those documents
        const newDocs = newDocumentsParam.split(",");
        setSelectedDocumentIds(newDocs);
      } else {
        // Otherwise select all documents
        setSelectedDocumentIds(uniqueDocumentIds);
      }
    }
  }, [selectedDocumentIds.length, jobDocs?.length]);

  const fetchPresignedUrl = async () => {
    const domain =
      process.env.NODE_ENV === "production"
        ? "https://www.docuclipper.com"
        : "http://localhost:8080";

    const documentId =
      selectedDocumentId ||
      (tls.tls && tls.tls.length > 0 && tls.tls[0]?.documentId) ||
      null;

    if (!documentId) {
      return "";
    }

    const jwt = getJwt();
    if (!jwt) {
      return "";
    }

    const response = await fetch(
      `${domain}/api/v1/protected/document/${documentId}/downloads3`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      },
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch presigned URL: ${response.statusText}`);
    }

    const data = await response.json();
    return data.url as string;
  };

  const tlsByDocId = groupBy(tls.tls, "documentId");

  useEffect(() => {
    dispatch(
      fetchTls(true, {
        l1: selectedL1,
        l2: selectedL2,
        filters: selectedDocumentId
          ? [
              {
                field: "documentId",
                operator: "equals",
                value: selectedDocumentId,
              },
              {
                field: "fieldName",
                operator: "equals",
                value: "formMode",
              },
            ]
          : [],
        pagination: {
          page: 1,
          pageSize: 100,
        },
      }),
    );
    dispatch(fetchIntegrations());
  }, [selectedDocumentId, selectedL1, selectedL2]);

  // console.log({ selectedDocumentId });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const presigned = await fetchPresignedUrl();
        if (cancelled) return;
        if (presigned) {
          setPdfURL(`${presigned}#page=${selectedPageNumber}&toolbar=0`);
        } else {
          setPdfURL("");
        }
      } catch (err) {
        if (!cancelled) {
          console.error("ExportForm: failed to fetch presigned URL", err);
          setPdfURL("");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedPageNumber, tls.tls.length, selectedDocumentId]);

  const { selectedL1: l1, selectedL2: l2 } = useSelector(
    (state: ReduxState) => state.TagLevel,
  );
  const selectedTl =
    !isNullOrUndefined(selectedDocumentId) &&
    !isNullOrUndefined(selectedRowNumber)
      ? tls.tls.filter((x) => x.documentId === selectedDocumentId)[
          selectedRowNumber as number
        ]
      : null;

  // const formDoc: FormDoc = selectedTl
  //   ? JSON.parse(selectedTl.row as any)
  //   : null;

  const formDoc = selectedTl?.row;

  // Memoize the entries of formDoc to prevent unnecessary re-renders
  const formDocEntries = React.useMemo(() => {
    return Object.entries(formDoc || {});
  }, [selectedTl]);

  // if (tls.tls.length === 0) {
  //   return null;
  // }

  // console.log({
  //   formDoc,
  //   selectedTl,
  //   selectedDocumentId,
  //   selectedRowNumber,
  //   tls,
  // });
  // if (!formDoc) {
  //   console.log("no formdoc");
  //   return null;
  // }

  const handleOnSubmit = (values) => {
    let formDocData: FormDoc = {};

    let alertValidation = false;

    if (!alertValidation && selectedTl && selectedDocumentId) {
      formDocData = selectedTl ? (selectedTl.row as any) : null;

      formDocData = updateObjKeyValue(formDocData, values);

      const fn =
        job && job.id
          ? updateJobTransaction(job?.id, selectedTl.id as any, {
              newValue: JSON.stringify(formDocData),
            })
          : updateJobTransactionTagLevel(selectedTl.id as any, {
              newValue: JSON.stringify(formDocData),
            });

      fn.then((data) => {
        // const currentDocumentId = selectedDocumentId;
        // const currentRowNumber = selectedRowNumber;
        // const currentPageNumber = selectedPageNumber;
        // dispatch(
        //   fetchTls(true, () => {
        //     dispatch(setSelectedDocumentId(currentDocumentId));
        //     if (currentRowNumber !== null) {
        //       dispatch(setSelectedRowNumber(currentRowNumber));
        //     }
        //     setSelectedPageNumber(currentPageNumber);
        //   })
        // );
      }).catch(() =>
        dispatch(
          createAlert({
            message: "Error updating form",
            timeout: 0,
            type: "error",
          }),
        ),
      );
    }
  };

  const handleDownload = () => {
    sendGAEvent({
      action: "exportDownloadClick",
      category: "export",
      label: "form",
    });
    const selectedIds =
      selectedDocumentIds.length > 0
        ? selectedDocumentIds
        : selectedDocumentId
          ? [selectedDocumentId]
          : [];

    setIsDownloading(true);
    downloadSpreadsheetHelper(
      job,
      l1,
      l2,
      "Form",
      exportTo,
      {
        dateFormat: "YYYY-MM-DD",
        fileType: exportTo.toUpperCase(),
      },
      selectedIds,
      [],
      [],
      [
        {
          field: "fieldName",
          operator: "equals",
          value: "formMode",
        },
      ],
      "date",
      "asc",
      "csv" as any,
    ).finally(() => {
      setIsDownloading(false);
    });
  };

  let cta = "Download";
  if (["custom-basic", "xlsx"].includes(exportTo)) {
    // cta = `Download Excel (${exportTo === "xlsx" ? exportTo.toUpperCase() : "CSV"
    //   }) - ${tls.tls.length} items`;
    cta = `Download Excel`;
  }

  const handleFormAction = () => {
    const currentParams = new URLSearchParams(window.location.search);
    history.push(
      `/extract/form/${
        currentParams.toString() ? "?" + currentParams.toString() : ""
      }`,
    );
  };

  // Empty State Component
  const EmptyStateComponent = () => (
    <div
      className="d-flex flex-column align-items-center justify-content-center"
      style={{ minHeight: "400px" }}
    >
      <div className="text-center mb-4">
        <FontAwesomeIcon
          icon="file-alt"
          size="4x"
          className="text-muted mb-3"
        />
        <h4 className="text-muted">No Forms Found</h4>
        <p className="text-muted">
          Get started by adding your forms or documents to extract form data
        </p>
      </div>

      <div>
        <Button
          variant="primary"
          size="lg"
          onClick={handleFormAction}
          className="d-flex flex-column align-items-center p-4"
          style={{ minWidth: "250px" }}
        >
          <FontAwesomeIcon icon="file-alt" size="2x" className="mb-2" />
          <strong>Add Forms</strong>
        </Button>
      </div>
    </div>
  );

  // Show empty state if no documents found
  if ((!jobDocs || jobDocs.length === 0) && !loading) {
    return (
      <Container fluid>
        <div className="p-4">
          <Row>
            <Col md="12">
              <EmptyStateComponent />
            </Col>
          </Row>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid>
      {/* <div className="mx-2">
        <Alert variant="info">
          We just added support for converting form. Email{" "}
          <a href="mailto:support@docuclipper.com">support@docuclipper.com</a>{" "}
          if you run into any issues or have feedback.
        </Alert>
      </div> */}
      <Row className="justify-content-end mb-3 p-2">
        {urlL1 && urlL2 && (
          <Button variant="link" onClick={handleAddForms}>
            <FontAwesomeIcon icon="plus" />
            <span className="m-2">Add Forms</span>
          </Button>
        )}
        <Button
          className="btn--report-bad-job"
          variant="link"
          onClick={() => {
            dispatch(setHelpDrawerOpen(true));
          }}
        >
          <FontAwesomeIcon icon="exclamation-triangle" />
          <span className="m-2">Report Issue</span>
        </Button>
        <HelpDrawer docType="form" />
      </Row>
      <Row>
        <Col md="6">
          <DocumentSelector />
          {selectedDocumentId && tlsByDocId[selectedDocumentId] && (
            <FormGroup className="mb-3">
              <Form.Label>
                <strong>Select Form</strong>
              </Form.Label>
              <Form.Control
                as="select"
                value={selectedRowNumber || 0}
                onChange={(e) => {
                  const selectedIndex = parseInt(e.target.value, 10);
                  const selectedTransaction =
                    tlsByDocId[selectedDocumentId][selectedIndex];
                  // dispatch(setSelectedDocumentId(selectedDocumentId));
                  dispatch(setSelectedRowNumber(selectedIndex));
                  setSelectedPageNumber(selectedTransaction.pageNumber + 1);
                }}
              >
                {tlsByDocId[selectedDocumentId].map((_, index) => (
                  <option key={`form-${index}`} value={index}>
                    Form {index + 1}
                  </option>
                ))}
              </Form.Control>
            </FormGroup>
          )}
          <PDFViewer key={selectedPageNumber} url={pdfURL} />
        </Col>
        <Col md="6">
          {/* <Row>
            <Col>
              <FormGroup>
                <strong>
                  <Form.Label>Export To</Form.Label>
                </strong>
              
              </FormGroup>
            </Col>
          </Row> */}
          <Row className="justify-content-end mb-4">
            <Button
              className="mx-4 btn--still-want-cancel"
              variant="primary"
              onClick={() => setShowModal(true)}
              disabled={isDownloading}
            >
              {isDownloading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="mx-2"
                  />
                  Downloading...
                </>
              ) : (
                cta
              )}
            </Button>
          </Row>
          <Formik
            key={selectedDocumentId}
            enableReinitialize={true}
            initialValues={formDocEntries.reduce((acc, [key, value]) => {
              acc[`key-${key}`] = key;
              acc[`value-${key}`] = value;
              return acc;
            }, {})}
            onSubmit={handleOnSubmit}
          >
            {({ values }) => {
              return (
                <>
                  <div style={{ height: "95vh", overflow: "scroll" }}>
                    <Row>
                      <Col md={6}>
                        <strong>
                          <Form.Label>Key</Form.Label>
                        </strong>
                      </Col>
                      <Col md={6}>
                        <strong>
                          <Form.Label>Value</Form.Label>
                        </strong>
                      </Col>
                    </Row>

                    {formDocEntries.map(([key, value]) => (
                      <Row key={key}>
                        <Col md={6}>
                          <FormGroup>
                            <Field
                              placeholder=""
                              name={`key-${key}`}
                              type="text"
                              component={formikInput}
                              defaultValue={key}
                              value={values[`key-${key}`] || ""}
                              onBlur={() => handleOnSubmit(values)}
                              // readOnly
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Field
                              placeholder=""
                              name={`value-${key}`}
                              type="text"
                              component={formikInput}
                              defaultValue={value}
                              value={values[`value-${key}`] || ""}
                              onBlur={() => handleOnSubmit(values)}
                              // readOnly
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                    ))}
                  </div>
                </>
              );
            }}
          </Formik>
        </Col>
      </Row>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header
          closeButton
          onPointerEnterCapture={() => {}}
          onPointerLeaveCapture={() => {}}
          placeholder={undefined}
        >
          <Modal.Title>Download</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <div className="font-weight-bold">Documents</div>
            <DocumentsDropdown />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              handleDownload();
              setShowModal(false);
            }}
          >
            Download
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};
export default ExportForm;
